import React, { useState, useEffect, useRef } from 'react';
import TextInput from '../atoms/TextInput';
import Button from '../atoms/Button';
import { useChat } from '../../context/ChatContext';
import './ChatFooter.css';



const ChatFooter = ({ interviewComplete }) => {
  const [message, setMessage] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const recordingTimerRef = useRef(null);

  const {
    sendMessage,
    isLoading,
    isSpeaking,
    selectedVoice,
    hasPdfContent,
    hasTechRequirements,
    interviewType,
    speakMessage,
    isRecording,
    startVoiceRecording,
    stopVoiceRecording
  } = useChat();

  // Determine if we have the required content for the current interview type
  const hasRequiredContent = interviewType === 'behavioral' ? hasPdfContent : hasTechRequirements;

  // Handle recording timer
  useEffect(() => {
    if (isRecording) {
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      // Clear the timer when recording stops
      clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }
    return () => clearInterval(recordingTimerRef.current);
  }, [isRecording]);

  // Format recording time as minutes:seconds
  const formatRecordingTime = () => {
    const minutes = Math.floor(recordingTime / 60);
    const seconds = recordingTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !isLoading && !isSpeaking && !interviewComplete && hasRequiredContent) {
      sendMessage(message);
      setMessage('');
    }
  };

  // Voice button handler
  const handleVoiceButton = () => {
    if (interviewComplete || !hasRequiredContent) return;
    
    if (isRecording) {
      stopVoiceRecording();
    } else {
      startVoiceRecording();
    }
  };

  // Prevent form submission while recording
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && isRecording) {
      e.preventDefault();
      stopVoiceRecording();
    }
  };

  // Test audio function
  const testAudio = () => {
    try {
      const testMessage = "This is a test of the text-to-speech functionality. If you can hear this, audio is working correctly.";
      console.log("Testing audio with voice:", selectedVoice?.name || "default");
      speakMessage(testMessage);
    } catch (err) {
      // Optionally handle error
    }
  };

  // Toggle debug info
  const toggleDebug = () => {
    setShowDebug(!showDebug);
  };

  // If interview is complete, only show the debug button
  if (interviewComplete) {
    return (
      <>
        <form className="chat-footer chat-footer-complete">
          <Button 
            type="button"
            variant="outline" 
            size="small" 
            className="chat-footer-debug"
            onClick={toggleDebug}
            title="Toggle debug info"
          >
            <span className="footer-icon">🛠️</span>
          </Button>
        </form>

        {showDebug && (
          <div className="chat-debug-panel">
            <div className="debug-info">
              <p><strong>Interview Status:</strong> Complete</p>
              <p><strong>Interview Type:</strong> {interviewType}</p>
              <p><strong>Audio State:</strong> {isSpeaking ? 'Speaking' : 'Silent'}</p>
              <p><strong>Selected Voice:</strong> {selectedVoice ? `${selectedVoice.name} (${selectedVoice.lang})` : 'None'}</p>
              <p><strong>Available Voices:</strong> {window.speechSynthesis ? window.speechSynthesis.getVoices().length : 'Not supported'}</p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={testAudio}
              disabled={isSpeaking}
            >
              Test Audio
            </Button>
          </div>
        )}
      </>
    );
  }

  // Check if we have the required content
  const inputDisabled = isRecording || isSpeaking || interviewComplete || !hasRequiredContent;
  const buttonDisabled = !hasRequiredContent || isSpeaking || interviewComplete;
  
  const getPlaceholderText = () => {
    if (isRecording) {
      return `Recording${recordingTime > 0 ? ` ${formatRecordingTime()}` : ''}... (click mic to stop)`;
    }
    if (interviewComplete) {
      return "Interview completed";
    }
    if (!hasRequiredContent) {
      return interviewType === 'behavioral' 
        ? "Upload a PDF to start the interview" 
        : "Enter technical requirements to start the interview";
    }
    return "Type a message...";
  };

  const getButtonTitle = () => {
    if (!hasRequiredContent) {
      return interviewType === 'behavioral' 
        ? "Upload a PDF to enable recording" 
        : "Enter technical requirements to enable recording";
    }
    return isRecording ? "Stop recording" : "Start recording";
  };

  const getSendButtonTitle = () => {
    if (!hasRequiredContent) {
      return interviewType === 'behavioral' 
        ? "Upload a PDF to enable sending messages" 
        : "Enter technical requirements to enable sending messages";
    }
    return "Send message";
  };

  return (
    <>
      <form className="chat-footer" onSubmit={handleSubmit}>
        <Button 
          type="button"
          variant="outline" 
          size="small" 
          className={`chat-footer-button ${isRecording ? 'recording' : ''}`}
          onClick={handleVoiceButton}
          disabled={buttonDisabled} 
          aria-label={isRecording ? "Stop recording" : "Start recording"}
          title={getButtonTitle()}
        >
          <span className="footer-icon">{isRecording ? '⏹️' : '🎤'}</span>
        </Button>
        <TextInput
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={getPlaceholderText()}
          className="chat-footer-input"
          disabled={inputDisabled}
          onKeyDown={onKeyDown}
        />
        {message && (
          <Button
            type="button"
            variant="outline"
            size="small"
            className="chat-footer-debug"
            onClick={() => setMessage('')}
            title="Clear input"
          >
            <span className="footer-icon">✖️</span>
          </Button>
        )}
        <Button 
          type="submit" 
          variant="primary" 
          size="small"
          className="chat-footer-send"
          disabled={!message.trim() || isLoading || buttonDisabled || isRecording}
          title={getSendButtonTitle()}
        >
          <span className="footer-icon">➤</span>
        </Button>
        <Button 
          type="button"
          variant="outline" 
          size="small" 
          className="chat-footer-debug"
          onClick={toggleDebug}
          title="Toggle debug info"
        >
          <span className="footer-icon">🛠️</span>
        </Button>
      </form>
      
      {showDebug && (
        <div className="chat-debug-panel">
          <div className="debug-info">
            <p><strong>Interview Type:</strong> {interviewType}</p>
            <p><strong>PDF Content:</strong> {hasPdfContent ? 'Loaded' : 'Not loaded'}</p>
            <p><strong>Tech Requirements:</strong> {hasTechRequirements ? 'Set' : 'Not set'}</p>
            <p><strong>Has Required Content:</strong> {hasRequiredContent ? 'Yes' : 'No'}</p>
            <p><strong>Audio State:</strong> {isSpeaking ? 'Speaking' : 'Silent'}</p>
            <p><strong>Recording State:</strong> {isRecording ? 'Recording' : 'Not recording'}</p>
            <p><strong>Selected Voice:</strong> {selectedVoice ? `${selectedVoice.name} (${selectedVoice.lang})` : 'None'}</p>
            <p><strong>Available Voices:</strong> {window.speechSynthesis ? window.speechSynthesis.getVoices().length : 'Not supported'}</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={testAudio}
            disabled={isSpeaking}
          >
            Test Audio
          </Button>
        </div>
      )}
    </>
  );
};

export default ChatFooter; 